/**
 * Project Staffing Report — full page, booking-driven.
 * Access Evo Design System compliant.
 *
 * Primary personas:
 *   Project Manager   — confirm project is fully resourced before delivery starts.
 *   Resource Manager  — see outstanding demand across the portfolio to prioritise gaps.
 *   PMO               — weekly governance across all projects, staffing health at a glance.
 *   Practice Leads    — see where their skill set is in demand across the portfolio.
 *   Ops Directors     — review overall delivery risk from understaffing.
 */
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  format,
  addDays,
  addWeeks,
  parseISO,
  differenceInCalendarDays,
  isAfter,
  isBefore,
  startOfDay,
  max as dateMax,
  min as dateMin,
} from 'date-fns';
import {
  BarChart2,
  ChevronUp,
  ChevronDown,
  Download,
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Users,
  PlusCircle,
  LayoutGrid,
  List,
  Loader2,
  X,
  SlidersHorizontal,
  Settings2,
  RotateCcw,
  GripVertical,
} from 'lucide-react';


import { FeatureFeedbackCollector } from '@/components/FeedbackCollector';
import { HelpButton } from '@/components/help/HelpButton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multiselect';
import { Sheet, SheetContent, SheetTitle, SheetDescription, SheetHeader, SheetFooter } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Check, Filter } from 'lucide-react';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import { useScheduler } from '@/contexts/SchedulerContext';
import { useScreenData } from '@/hooks/useScreenData';
import { useReportScope } from '@/hooks/useReportScope';
import { useScrollbarGutter } from '@/hooks/useScrollbarGutter';
import { useCompany } from '@/contexts/CompanyContext';
import { bookingsApi } from '@/services/api';
import { mapApiBookingToBooking } from '@/hooks/use-date-range-bookings';
import { usePermissions } from '@/contexts/PermissionsContext';
import { ComprehensiveBookingModal } from '@/components/calendar/ComprehensiveBookingModal';
import type { Booking, Resource, ProjectDetails } from '@/types';
import {
  RAW_TABLE_CONTAINER_CLASS,
  RAW_TABLE_SCROLL_CLASS,
  TABLE_HEADER_ROW_CLASS,
  TABLE_ROW_CLASS,
  RAW_TABLE_CLASS,
} from '@/lib/table-styles';

// ─── Constants ────────────────────────────────────────────────────────────────

const WORKING_HOURS_PER_DAY = 8;
const DEFAULT_DATE_RANGE_WEEKS = 12;
const AT_RISK_WEEKS = 4;

// ─── Types ────────────────────────────────────────────────────────────────────

type StaffingStatusFilter = 'all' | 'gaps' | 'fully-staffed' | 'overstaffed';
type ViewMode = 'card' | 'list';
type ProjectStatus = 'active' | 'pipeline' | 'on-hold' | 'completed';

export interface RoleRequirement {
  /** Synthetic key: projectId + role name + index */
  id: string;
  projectId: string;
  projectName: string;
  clientName: string;
  projectStartDate: Date | null;
  projectEndDate: Date | null;
  roleName: string;
  grade: string;
  requiredHeadcount: number;
  bookedResources: Array<{
    id: string;
    name: string;
    initials: string;
    photoUrl?: string;
    allocationPct: number;
    bookingType: 'confirmed' | 'planned';
  }>;
  bookedCount: number;
  gapCount: number;
  bookingTypeSummary: 'confirmed' | 'planned' | 'mixed' | 'none';
  status: 'filled' | 'partially-filled' | 'unfilled' | 'overstaffed';
  totalAllocationPct: number;
}

export interface ProjectStaffingRow {
  projectId: string;
  projectName: string;
  clientName: string;
  projectStartDate: Date | null;
  projectEndDate: Date | null;
  startDateStr: string;
  endDateStr: string;
  projectStatus: ProjectStatus;
  staffingStatus: 'fully-staffed' | 'partially-staffed' | 'unfilled-roles' | 'overstaffed' | 'pipeline';
  unfilledRoleCount: number;
  totalRoles: number;
  roleRequirements: RoleRequirement[];
  hasConflict: boolean;
  conflictCount: number;
  isAtRisk: boolean;
  /** Days until project start (negative = already started) */
  daysUntilStart: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getInitials(name: string): string {
  const parts = String(name ?? '').trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function workingDaysInInterval(start: Date, end: Date): number {
  let count = 0;
  const cursor = new Date(start);
  while (cursor <= end) {
    const dow = cursor.getDay();
    if (dow !== 0 && dow !== 6) count++;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
}

function durationFactor(durationType: Booking['durationType']): number {
  switch (durationType) {
    case 'half-day':
    case 'half-day-am':
    case 'half-day-pm':
      return 0.5;
    default:
      return 1;
  }
}

function bookingHoursInRange(booking: Booking, rangeStart: Date, rangeEnd: Date): number {
  const bStart = parseISO(booking.startTime);
  let bEnd     = parseISO(booking.endTime);

  // For day-based bookings where endTime <= startTime (e.g. single-day
  // bookings stored with the same timestamp for start and end), treat the
  // booking as spanning at least one full day.
  if (booking.durationType !== 'hourly' && bEnd.getTime() <= bStart.getTime()) {
    bEnd = addDays(startOfDay(bStart), 1);
  }

  const overlapStart = dateMax([bStart, rangeStart]);
  const overlapEnd   = dateMin([bEnd,   rangeEnd]);
  if (overlapStart >= overlapEnd) return 0;

  // Hourly bookings: derive total hours from the start/end time span,
  // then pro-rate for partial overlap with the date range.
  if (booking.durationType === 'hourly') {
    const bookingMs = bEnd.getTime() - bStart.getTime();
    if (bookingMs > 0) {
      const totalHours = bookingMs / (1000 * 60 * 60);
      const overlapMs  = overlapEnd.getTime() - overlapStart.getTime();
      return totalHours * (overlapMs / bookingMs);
    }
    // Genuine zero-hour hourly booking (start == end) contributes nothing —
    // never fall through to the day-based full-day calculation below.
    return 0;
  }

  // Day-based bookings: subtract 1 ms so the working-day count treats the
  // end as inclusive of the last booked day rather than spilling into the
  // following day.
  const inclusiveEnd = new Date(overlapEnd.getTime() - 1);
  if (inclusiveEnd < overlapStart) return 0;
  const days = workingDaysInInterval(overlapStart, inclusiveEnd);
  return days * WORKING_HOURS_PER_DAY * durationFactor(booking.durationType);
}

function formatDateRange(from: Date, to: Date): string {
  return `${format(from, 'dd MMM yyyy')} — ${format(to, 'dd MMM yyyy')}`;
}

function formatShortDate(d: Date | null): string {
  if (!d) return '—';
  return format(d, 'dd MMM yyyy');
}

function computeDateRange(weeks: number): { from: Date; to: Date } {
  const today = startOfDay(new Date());
  return { from: today, to: addDays(addWeeks(today, weeks), -1) };
}

// ─── Segmented control ────────────────────────────────────────────────────────

interface SegmentedProps<T extends string> {
  options: { label: string; value: T; icon?: React.ReactNode }[];
  value: T;
  onChange: (v: T) => void;
}
function Segmented<T extends string>({ options, value, onChange }: SegmentedProps<T>) {
  return (
    <div className="inline-flex items-center gap-1.5 shrink-0">
      {options.map((o) => (
        <Button
          key={o.value}
          type="button"
          variant={o.value === value ? 'default' : 'outline'}
          size="sm"
          className="h-8"
          onClick={() => onChange(o.value)}
          aria-pressed={o.value === value}
        >
          {o.icon}
          {o.label}
        </Button>
      ))}
    </div>
  );
}

// ─── Sortable column header ───────────────────────────────────────────────────

interface SortHeaderProps {
  label: string;
  col: string;
  sortCol: string;
  sortDir: 'asc' | 'desc';
  onSort: (col: string) => void;
  align?: 'left' | 'right';
  minWidth?: number;
}
function SortHeader({ label, col, sortCol, sortDir, onSort, align = 'left', minWidth }: SortHeaderProps) {
  const active = sortCol === col;
  return (
    <th
      onClick={() => onSort(col)}
      className="cursor-pointer select-none px-3 py-2.5 font-medium text-[12px] text-muted-foreground whitespace-nowrap group"
      style={{ textAlign: align, minWidth }}
    >
      <span
        className="inline-flex items-center gap-1"
        style={{ justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}
      >
        {label}
        <span className={active ? 'text-foreground' : 'opacity-0 group-hover:opacity-50'}>
          {active && sortDir === 'asc' ? (
            <ChevronUp className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )}
        </span>
      </span>
    </th>
  );
}

// ─── Summary stat card ────────────────────────────────────────────────────────

interface StatCardProps {
  value: string | number;
  label: string;
  subLabel: string;
  valueColour?: 'default' | 'teal' | 'destructive' | 'warning';
  isFilterable?: boolean;
  isActive?: boolean;
  onClick?: () => void;
}
function StatCard({
  value,
  label,
  subLabel,
  valueColour = 'default',
  isFilterable,
  isActive,
  onClick,
}: StatCardProps) {
  const colourClass =
    valueColour === 'teal'
      ? 'text-[var(--accent-teal-900,oklch(0.30_0.07_194))] dark:text-[var(--teal-400)]'
      : valueColour === 'destructive'
      ? 'text-destructive'
      : valueColour === 'warning'
      ? 'text-warning-500'
      : 'text-foreground';

  return (
    <div
      className={[
        'flex-1 flex flex-col gap-1 rounded-[var(--radius-md)] border bg-card px-4 py-4 min-w-0 transition-all duration-150',
        isFilterable ? 'cursor-pointer select-none hover:border-primary/50 hover:shadow-sm' : '',
        isActive ? 'border-primary ring-2 ring-primary/20 shadow-sm' : 'border-border',
      ].join(' ')}
      role={isFilterable ? 'button' : undefined}
      tabIndex={isFilterable ? 0 : undefined}
      aria-pressed={isFilterable ? isActive : undefined}
      onClick={isFilterable ? onClick : undefined}
      onKeyDown={
        isFilterable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
    >
      <div className="flex items-start justify-between gap-1">
        <span className={`text-2xl font-semibold leading-none ${colourClass}`}>{value}</span>
        {isFilterable && isActive && (
          <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-primary/10 text-primary shrink-0 mt-0.5">
            <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
              <path
                d="M1.5 1.5L6.5 6.5M6.5 1.5L1.5 6.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </span>
        )}
      </div>
      <span className="text-xs text-muted-foreground mt-1">{label}</span>
      <span className="text-xs text-muted-foreground">{subLabel}</span>
      {isFilterable && !isActive && (
        <span className="text-[10px] text-primary/70 mt-1 font-medium">Click to filter ↓</span>
      )}
      {isFilterable && isActive && (
        <span className="text-[10px] text-primary mt-1 font-medium">
          Filtering active · click to clear
        </span>
      )}
    </div>
  );
}

// ─── Staffing status badge ────────────────────────────────────────────────────

function StaffingBadge({
  status,
  unfilledCount = 0,
}: {
  status: ProjectStaffingRow['staffingStatus'];
  unfilledCount?: number;
}) {
  switch (status) {
    case 'fully-staffed':
      return (
        <Badge variant="success" accent="success" className="text-[10px] px-1.5 py-0 whitespace-nowrap">
          Fully staffed
        </Badge>
      );
    case 'partially-staffed':
      return (
        <Badge variant="warning" accent="warning" className="text-[10px] px-1.5 py-0 whitespace-nowrap">
          Partially staffed
        </Badge>
      );
    case 'unfilled-roles':
      return (
        <Badge
          variant="destructive"
          accent="destructive"
          className="text-[10px] px-1.5 py-0 whitespace-nowrap"
        >
          {unfilledCount > 0
            ? `${unfilledCount} unfilled role${unfilledCount !== 1 ? 's' : ''}`
            : 'Unfilled roles'}
        </Badge>
      );
    case 'overstaffed':
      return (
        <Badge variant="warning" accent="warning" className="text-[10px] px-1.5 py-0 whitespace-nowrap">
          Overstaffed
        </Badge>
      );
    case 'pipeline':
      return (
        <Badge variant="info" accent="info" className="text-[10px] px-1.5 py-0 whitespace-nowrap">
          Pipeline
        </Badge>
      );
    default:
      return null;
  }
}

// ─── Role status badge ────────────────────────────────────────────────────────

function RoleStatusBadge({ status }: { status: RoleRequirement['status'] }) {
  switch (status) {
    case 'filled':
      return (
        <Badge variant="success" accent="success" className="text-[10px] px-1.5 py-0">
          Filled
        </Badge>
      );
    case 'partially-filled':
      return (
        <Badge variant="warning" accent="warning" className="text-[10px] px-1.5 py-0">
          Partially filled
        </Badge>
      );
    case 'unfilled':
      return (
        <Badge variant="destructive" accent="destructive" className="text-[10px] px-1.5 py-0">
          Unfilled
        </Badge>
      );
    case 'overstaffed':
      return (
        <Badge variant="warning" accent="warning" className="text-[10px] px-1.5 py-0">
          Overstaffed
        </Badge>
      );
    default:
      return null;
  }
}

// ─── Booking type pill ────────────────────────────────────────────────────────

function BookingTypePill({ type }: { type: 'confirmed' | 'planned' | 'mixed' | 'none' }) {
  if (type === 'confirmed') {
    return (
      <Badge variant="success" accent="success" className="text-[10px] px-1.5 py-0 whitespace-nowrap">
        Confirmed
      </Badge>
    );
  }
  if (type === 'planned') {
    return (
      <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-muted-foreground whitespace-nowrap">
        Planned
      </Badge>
    );
  }
  if (type === 'mixed') {
    return (
      <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-muted text-muted-foreground border-border whitespace-nowrap">
        Mixed
      </Badge>
    );
  }
  return null;
}

// ─── Gap cell ─────────────────────────────────────────────────────────────────

function GapCell({ req }: { req: RoleRequirement }) {
  if (req.status === 'unfilled') {
    return <span className="text-[12px] text-destructive font-medium">Unfilled</span>;
  }
  if (req.status === 'filled') {
    return (
      <span className="text-[12px] text-[var(--accent-teal-900,oklch(0.30_0.07_194))] dark:text-[var(--teal-400)] font-medium">
        {req.bookedCount} of {req.requiredHeadcount} filled
      </span>
    );
  }
  if (req.status === 'partially-filled') {
    return (
      <span className="text-[12px] text-warning-500 font-medium">
        {req.bookedCount} of {req.requiredHeadcount} filled
      </span>
    );
  }
  if (req.status === 'overstaffed') {
    return <span className="text-[12px] text-warning-500 font-medium">{req.totalAllocationPct}%</span>;
  }
  return <span className="text-[12px] text-muted-foreground">—</span>;
}

// ─── Data hook ────────────────────────────────────────────────────────────────

function useProjectStaffingData(
  dateFrom: Date,
  dateTo: Date,
  selectedStatuses: ProjectStatus[],
  selectedProjects: string[],
  selectedClients: string[],
  selectedDepartments: string[],
  selectedTeams: string[],
  selectedRoles: string[],
  staffingStatusFilter: StaffingStatusFilter,
  reportBookings: Booking[],
) {
  const { resources: allResources, projectDetails, clients, settings, schedulerGroups } =
    useScheduler();

  // Scope visible resources by the role's report "View Access" (all / team / own)
  const { scopeResources } = useReportScope('projectStaffingReport');
  const resources = useMemo(() => scopeResources(allResources), [scopeResources, allResources]);
  const bookings = reportBookings;

  return useMemo(() => {
    const today = startOfDay(new Date());
    const atRiskCutoff = addWeeks(today, AT_RISK_WEEKS);

    // Real resources only
    const realResources = resources.filter(
      (r) => r.id !== 'unallocated' && r.status !== 'inactive',
    );

    // ── Resolve client names ───────────────────────────────────────────────
    const clientMap = new Map<string, string>();
    for (const c of clients) {
      clientMap.set(String(c.id), c.name);
    }

    // ── Filter + normalise project details ─────────────────────────────────
    const statusMapping: Record<string, ProjectStatus> = {
      active: 'active',
      pipeline: 'pipeline',
      'on-hold': 'on-hold',
      'on hold': 'on-hold',
      completed: 'completed',
    };

    const projects = projectDetails.filter((p) => {
      // Soft-deleted projects would otherwise fall through statusMapping's 'active' default
      if (p.isDeleted) return false;
      const ps = statusMapping[p.status?.toLowerCase() ?? ''] ?? 'active';
      if (selectedStatuses.length > 0 && !selectedStatuses.includes(ps)) return false;
      if (selectedProjects.length > 0 && !selectedProjects.includes(String(p.id))) return false;
      const clientName = p.clientName ?? clientMap.get(String(p.clientId)) ?? '—';
      if (selectedClients.length > 0 && !selectedClients.includes(clientName)) return false;
      return true;
    });

    // ── Bucket bookings by projectId ───────────────────────────────────────
    const bookingsByProject: Record<string, Booking[]> = {};
    for (const b of bookings) {
      if (b.status === 'cancelled') continue;
      const pid = String(b.projectId ?? b.projectName ?? '');
      if (!pid) continue;
      if (!bookingsByProject[pid]) bookingsByProject[pid] = [];
      bookingsByProject[pid].push(b);
    }

    // Also key by project name for name-based matching
    const bookingsByProjectName: Record<string, Booking[]> = {};
    for (const b of bookings) {
      if (b.status === 'cancelled') continue;
      const pname = b.projectName ?? '';
      if (!pname) continue;
      if (!bookingsByProjectName[pname]) bookingsByProjectName[pname] = [];
      bookingsByProjectName[pname].push(b);
    }

    // ── Resolve resource name lookup ───────────────────────────────────────
    const resourceMap = new Map<string, Resource>();
    for (const r of realResources) {
      resourceMap.set(r.id, r);
    }

    // ── Build project staffing rows ────────────────────────────────────────
    const rows: ProjectStaffingRow[] = projects.map((p) => {
      const pid = String(p.id);
      const ps = statusMapping[p.status?.toLowerCase() ?? ''] ?? 'active';
      const clientName = p.clientName ?? clientMap.get(String(p.clientId ?? '')) ?? '—';

      const projStartDate = p.startDate ? parseISO(p.startDate) : null;
      const projEndDate = p.endDate ? parseISO(p.endDate) : null;

      const daysUntilStart = projStartDate
        ? differenceInCalendarDays(projStartDate, today)
        : 9999;

      // Gather bookings for this project (by id or name)
      const projBookings = [
        ...(bookingsByProject[pid] ?? []),
        ...(bookingsByProjectName[p.name] ?? []).filter(
          (b) => String(b.projectId ?? '') !== pid,
        ),
      ].filter((b) => {
        const bStart = parseISO(b.startTime);
        const bEnd = parseISO(b.endTime);
        return bStart < dateTo && bEnd > dateFrom;
      });

      // ── Build role requirements from unique roles booked on project ────
      // Group by the resource's *current* role (from the latest API data)
      // rather than the booking's requiredRole which may be stale.  Fall
      // back to requiredRole only when the resource cannot be resolved.
      const roleGroups: Map<string, Booking[]> = new Map();
      for (const b of projBookings) {
        const resource = resourceMap.get(b.resourceId ?? '');
        const roleKey = resource
          ? (resource.role || 'Unassigned')
          : (b.requiredRole ?? 'General');
        if (!roleGroups.has(roleKey)) roleGroups.set(roleKey, []);
        roleGroups.get(roleKey)!.push(b);
      }

      // If no bookings, create a placeholder "Unassigned" requirement
      if (roleGroups.size === 0 && ps !== 'completed') {
        roleGroups.set('Unassigned', []);
      }

      const roleRequirements: RoleRequirement[] = [];
      let reqIndex = 0;

      for (const [roleName, roleBkgs] of roleGroups.entries()) {
        // Unique resources booked into this role
        const uniqueResourceIds = new Set(
          roleBkgs.map((b) => b.resourceId ?? '').filter(Boolean),
        );
        const bookedResources = Array.from(uniqueResourceIds).map((rid) => {
          const r = resourceMap.get(rid);
          // Compute allocation % from bookings for this resource+role combo
          const rBkgs = roleBkgs.filter((b) => b.resourceId === rid);
          let hrs = 0;
          for (const b of rBkgs) {
            hrs += bookingHoursInRange(b, dateFrom, dateTo);
          }
          const totalDays = workingDaysInInterval(dateFrom, dateTo);
          const totalCap = totalDays * WORKING_HOURS_PER_DAY;
          const allocationPct =
            totalCap > 0 ? Math.min(200, Math.round((hrs / totalCap) * 100)) : 0;

          const hasConfirmed = rBkgs.some(
            (b) => b.status === 'confirmed' || b.status === 'completed',
          );
          const bookingType: 'confirmed' | 'planned' = hasConfirmed ? 'confirmed' : 'planned';

          return {
            id: rid,
            name: r?.name ?? rid,
            initials: getInitials(r?.name ?? rid),
            photoUrl: r?.photoUrl,
            allocationPct,
            bookingType,
          };
        });

        const bookedCount = bookedResources.length;

        // Total allocation % across all booked resources for this role
        const totalAllocationPct = bookedResources.reduce(
          (s, r) => s + r.allocationPct,
          0,
        );

        // Required headcount — default 1 if unspecified
        const requiredHeadcount = Math.max(1, bookedCount > 0 ? bookedCount : 1);

        // Booking type summary
        const hasConfirmedAny = bookedResources.some((r) => r.bookingType === 'confirmed');
        const hasPlannedAny = bookedResources.some((r) => r.bookingType === 'planned');
        const bookingTypeSummary: RoleRequirement['bookingTypeSummary'] =
          bookedCount === 0
            ? 'none'
            : hasConfirmedAny && hasPlannedAny
            ? 'mixed'
            : hasConfirmedAny
            ? 'confirmed'
            : 'planned';

        // Gap count
        const gapCount = Math.max(0, requiredHeadcount - bookedCount);

        // Status
        let status: RoleRequirement['status'];
        if (bookedCount === 0) {
          status = 'unfilled';
        } else if (totalAllocationPct > 100 * requiredHeadcount) {
          status = 'overstaffed';
        } else if (bookedCount >= requiredHeadcount) {
          status = 'filled';
        } else {
          status = 'partially-filled';
        }

        // Apply role/department filter
        if (selectedRoles.length > 0 && !selectedRoles.includes(roleName)) {
          reqIndex++;
          continue;
        }

        const resource0 = bookedCount > 0 ? resourceMap.get(bookedResources[0].id) : null;
        if (
          selectedDepartments.length > 0 &&
          resource0 &&
          !selectedDepartments.includes(resource0.department ?? '')
        ) {
          reqIndex++;
          continue;
        }

        if (
          selectedTeams.length > 0 &&
          resource0 &&
          !selectedTeams.includes(resource0.schedulerGroup ?? '')
        ) {
          reqIndex++;
          continue;
        }

        roleRequirements.push({
          id: `${pid}-${roleName}-${reqIndex}`,
          projectId: pid,
          projectName: p.name,
          clientName,
          projectStartDate: projStartDate,
          projectEndDate: projEndDate,
          roleName,
          grade:
            bookedResources.length > 0
              ? resourceMap.get(bookedResources[0].id)?.level ?? ''
              : '',
          requiredHeadcount,
          bookedResources,
          bookedCount,
          gapCount,
          bookingTypeSummary,
          status,
          totalAllocationPct,
        });
        reqIndex++;
      }

      // ── Compute project staffing status ──────────────────────────────
      const unfilledRoleCount = roleRequirements.filter(
        (r) => r.status === 'unfilled' || r.status === 'partially-filled',
      ).length;
      const hasOverstaffed = roleRequirements.some((r) => r.status === 'overstaffed');
      const hasAnyGap = unfilledRoleCount > 0;
      const allFilled = !hasAnyGap && roleRequirements.length > 0;

      let staffingStatus: ProjectStaffingRow['staffingStatus'];
      if (ps === 'pipeline') {
        staffingStatus = 'pipeline';
      } else if (hasAnyGap) {
        if (allFilled) {
          staffingStatus = 'fully-staffed';
        } else if (roleRequirements.some((r) => r.status === 'filled' || r.status === 'overstaffed')) {
          staffingStatus = 'partially-staffed';
        } else {
          staffingStatus = 'unfilled-roles';
        }
      } else if (hasOverstaffed) {
        staffingStatus = 'overstaffed';
      } else if (allFilled) {
        staffingStatus = 'fully-staffed';
      } else {
        staffingStatus = 'unfilled-roles';
      }

      // Precise unfilled count
      const realUnfilled = roleRequirements.filter((r) => r.status === 'unfilled').length;
      if (realUnfilled > 0) {
        staffingStatus = 'unfilled-roles';
      } else if (roleRequirements.some((r) => r.status === 'partially-filled')) {
        staffingStatus = 'partially-staffed';
      } else if (roleRequirements.every((r) => r.status === 'filled')) {
        staffingStatus = ps === 'pipeline' ? 'pipeline' : 'fully-staffed';
      }

      // Conflict detection (overlapping bookings on same resource for this project)
      let conflictCount = 0;
      const seenConflictPairs = new Set<string>();
      const byResource: Record<string, Booking[]> = {};
      for (const b of projBookings) {
        const rid = b.resourceId ?? '';
        if (!rid) continue;
        if (!byResource[rid]) byResource[rid] = [];
        byResource[rid].push(b);
      }
      for (const [, rBkgs] of Object.entries(byResource)) {
        const sorted = [...rBkgs].sort(
          (a, b) =>
            parseISO(a.startTime).getTime() - parseISO(b.startTime).getTime(),
        );
        for (let i = 0; i < sorted.length; i++) {
          for (let j = i + 1; j < sorted.length; j++) {
            if (
              parseISO(sorted[j].startTime) < parseISO(sorted[i].endTime)
            ) {
              const key = [sorted[i].id, sorted[j].id].sort().join('-');
              if (!seenConflictPairs.has(key)) {
                seenConflictPairs.add(key);
                conflictCount++;
              }
            }
          }
        }
      }

      const isAtRisk =
        projStartDate !== null &&
        daysUntilStart <= AT_RISK_WEEKS * 7 &&
        daysUntilStart >= 0 &&
        realUnfilled > 0;

      return {
        projectId: pid,
        projectName: p.name,
        clientName,
        projectStartDate: projStartDate,
        projectEndDate: projEndDate,
        startDateStr: formatShortDate(projStartDate),
        endDateStr: formatShortDate(projEndDate),
        projectStatus: ps,
        staffingStatus,
        unfilledRoleCount: realUnfilled,
        totalRoles: roleRequirements.length,
        roleRequirements,
        hasConflict: conflictCount > 0,
        conflictCount,
        isAtRisk,
        daysUntilStart,
      };
    });

    // ── Apply staffing status filter ───────────────────────────────────────
    const filtered = rows.filter((r) => {
      switch (staffingStatusFilter) {
        case 'gaps':
          return r.unfilledRoleCount > 0 || r.staffingStatus === 'partially-staffed';
        case 'fully-staffed':
          return r.staffingStatus === 'fully-staffed';
        case 'overstaffed':
          return r.staffingStatus === 'overstaffed';
        default:
          return true;
      }
    });

    // ── Summary stats ──────────────────────────────────────────────────────
    const totalProjects = filtered.length;
    const projectsWithGaps = filtered.filter((r) => r.unfilledRoleCount > 0).length;
    const totalUnfilled = filtered.reduce((s, r) => s + r.unfilledRoleCount, 0);
    const atRiskProjects = filtered.filter((r) => r.isAtRisk).length;

    // PRD §5.4 — Roles with Gaps: count of role requirements with gapCount > 0
    const rolesWithGaps = filtered.reduce(
      (s, r) => s + r.roleRequirements.filter((rr) => rr.gapCount > 0).length,
      0,
    );

    // PRD §5.4 — Fully Staffed: count of role requirements fully filled
    const fullyStaffedRoles = filtered.reduce(
      (s, r) => s + r.roleRequirements.filter((rr) => rr.status === 'filled').length,
      0,
    );

    // ── Future gaps (4–12 weeks) ───────────────────────────────────────────
    const futureGapCutoffStart = addWeeks(today, AT_RISK_WEEKS);
    const futureGapCutoffEnd = addWeeks(today, DEFAULT_DATE_RANGE_WEEKS);
    const futureGaps: Array<{
      projectId: string;
      projectName: string;
      roleName: string;
      startDate: Date | null;
      daysUntilStart: number;
    }> = [];

    for (const row of filtered) {
      if (!row.isAtRisk && row.unfilledRoleCount > 0 && row.projectStartDate) {
        const startD = row.projectStartDate;
        if (startD >= futureGapCutoffStart && startD <= futureGapCutoffEnd) {
          for (const req of row.roleRequirements) {
            if (req.status === 'unfilled') {
              futureGaps.push({
                projectId: row.projectId,
                projectName: row.projectName,
                roleName: req.roleName,
                startDate: startD,
                daysUntilStart: row.daysUntilStart,
              });
            }
          }
        }
      }
    }

    // ── Flat list rows (for list view) ────────────────────────────────────
    const flatRows: Array<{
      key: string;
      req: RoleRequirement;
      project: ProjectStaffingRow;
    }> = filtered.flatMap((project) =>
      project.roleRequirements.map((req) => ({
        key: req.id,
        req,
        project,
      })),
    );

    return {
      projects: filtered,
      allProjects: rows,
      flatRows,
      futureGaps,
      summary: {
        totalProjects,
        projectsWithGaps,
        totalUnfilled,
        atRiskProjects,
        rolesWithGaps,
        fullyStaffedRoles,
      },
      isEmpty: filtered.length === 0,
      allFullyStaffed:
        filtered.length > 0 && filtered.every((r) => r.staffingStatus === 'fully-staffed'),
    };
  }, [
    resources,
    bookings,
    projectDetails,
    clients,
    dateFrom,
    dateTo,
    selectedStatuses,
    selectedProjects,
    selectedClients,
    selectedDepartments,
    selectedTeams,
    selectedRoles,
    staffingStatusFilter,
  ]);
}

// ─── Role row inside a project card ──────────────────────────────────────────

interface RoleRowProps {
  req: RoleRequirement;
  onAddResource: (req: RoleRequirement) => void;
  onFindAvailable?: (req: RoleRequirement) => void;
}
function RoleRow({ req, onAddResource, onFindAvailable }: RoleRowProps) {
  const isUnfilled = req.status === 'unfilled';
  const isOver = req.status === 'overstaffed';

  return (
    <div className="py-3 border-b border-border/50 last:border-0">

      {/* Top row — role name + status */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <span className="text-[13px] font-medium text-foreground">{req.roleName}</span>
          {req.grade && (
            <span className="text-[11px] text-muted-foreground">· {req.grade}</span>
          )}
          <span className="text-[11px] text-muted-foreground">
            ×{req.requiredHeadcount}
          </span>
          <BookingTypePill type={req.bookingTypeSummary} />
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <RoleStatusBadge status={req.status} />
        </div>
      </div>

      {/* Booked resources */}
      {req.bookedResources.length > 0 && (
        <div className="flex items-center gap-2 mt-2">
          <div className="flex -space-x-1.5">
            {req.bookedResources.slice(0, 2).map((r) => (
              <Avatar key={r.id} className="h-6 w-6 border-2 border-background shrink-0">
                <AvatarImage src={r.photoUrl} alt={r.name} />
                <AvatarFallback className="text-[9px] font-semibold bg-primary/10 text-primary">
                  {r.initials}
                </AvatarFallback>
              </Avatar>
            ))}
            {req.bookedResources.length > 2 && (
              <span className="inline-flex items-center justify-center h-6 w-6 rounded-full border-2 border-background bg-muted text-[9px] font-medium text-muted-foreground">
                +{req.bookedResources.length - 2}
              </span>
            )}
          </div>
          <span className="text-[11px] text-muted-foreground">
            {req.bookedResources
              .slice(0, 2)
              .map((r) => r.name)
              .join(', ')}
            {req.bookedResources.length > 2 &&
              ` +${req.bookedResources.length - 2} more`}
          </span>
          {req.bookedResources.some((r) => r.allocationPct > 0) && (
            <span className="text-[11px] text-muted-foreground">
              · avg {Math.round(
                req.bookedResources.reduce((s, r) => s + r.allocationPct, 0) /
                  req.bookedResources.length,
              )}%
            </span>
          )}
        </div>
      )}

      {/* Action row */}
      <div className="flex items-center gap-3 mt-1.5 flex-wrap">
        {isUnfilled && (
          <span className="text-[11px] font-medium text-destructive">Unfilled</span>
        )}
        {isOver && (
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] font-medium text-warning-500">Over</span>
            <span className="text-[11px] text-muted-foreground">
              {req.totalAllocationPct}% total allocation
            </span>
          </div>
        )}
        {(isUnfilled || req.status === 'partially-filled') && (
          <>
            <button
              onClick={() => onAddResource(req)}
              className="inline-flex items-center gap-0.5 text-[11px] text-primary font-medium hover:underline cursor-pointer ml-auto"
              aria-label={`Add resource for ${req.roleName}`}
            >
              <PlusCircle className="h-3 w-3" />
              Add resource ›
            </button>
            {onFindAvailable && (
              <button
                onClick={() => onFindAvailable(req)}
                className="inline-flex items-center gap-0.5 text-[11px] text-primary font-medium hover:underline cursor-pointer"
                aria-label={`Find available resource for ${req.roleName}`}
              >
                <ExternalLink className="h-3 w-3" />
                Find available ›
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Project card ─────────────────────────────────────────────────────────────

interface ProjectCardProps {
  project: ProjectStaffingRow;
  highlighted?: boolean;
  onAddResource: (req: RoleRequirement) => void;
  onFindAvailable: (req: RoleRequirement) => void;
  onNavigateToCalendar: (projectId: string) => void;
  onNavigateToHeatmap: (projectId: string) => void;
}

function ProjectCard({
  project,
  highlighted,
  onAddResource,
  onFindAvailable,
  onNavigateToCalendar,
  onNavigateToHeatmap,
}: ProjectCardProps) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div
      id={`project-card-${project.projectId}`}
      className={[
        'flex flex-col rounded-[var(--radius-md)] border bg-card overflow-hidden transition-all duration-150',
        highlighted
          ? 'border-primary ring-2 ring-primary/20 shadow-md'
          : 'border-border',
      ].join(' ')}
    >
      {/* Card header */}
      <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-border/60">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[14px] font-medium text-foreground leading-tight">
              {project.projectName}
            </span>
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 whitespace-nowrap">
              {project.projectStatus === 'on-hold'
                ? 'On hold'
                : project.projectStatus.charAt(0).toUpperCase() + project.projectStatus.slice(1)}
            </Badge>
            {project.isAtRisk && (
              <span
                className="inline-flex items-center gap-0.5 text-[10px] font-medium text-warning-500"
                title={`Project start is in ${project.daysUntilStart} days with unfilled roles`}
              >
                <AlertTriangle className="h-3 w-3" />
                At risk
              </span>
            )}
          </div>
          <p className="text-[12px] text-muted-foreground mt-0.5">{project.clientName}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {project.startDateStr}
            {project.endDateStr !== '—' ? ` — ${project.endDateStr}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <StaffingBadge
            status={project.staffingStatus}
            unfilledCount={project.unfilledRoleCount}
          />
          <button
            onClick={() => setExpanded((v) => !v)}
            className="inline-flex items-center justify-center h-6 w-6 rounded text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? (
              <ChevronUp className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
          </button>
        </div>
      </div>

      {/* Card body — role requirements */}
      {expanded && (
        <div className="px-4 py-1">
          {project.roleRequirements.length === 0 ? (
            <p className="text-[12px] text-muted-foreground py-3">
              No role requirements defined for this project.
            </p>
          ) : (
            project.roleRequirements.map((req) => (
              <RoleRow
                key={req.id}
                req={req}
                onAddResource={onAddResource}
                onFindAvailable={onFindAvailable}
              />
            ))
          )}

        </div>
      )}

      {/* Card footer */}
      <div
        className="flex flex-col gap-0 bg-muted/30 border-t border-border/50"
        style={{ borderTop: expanded ? undefined : 'none' }}
      >
        {/* Nav links */}
        <div className="flex items-center gap-4 px-4 py-2.5">
          <button
            onClick={() => onNavigateToCalendar(project.projectId)}
            className="inline-flex items-center gap-1 text-[11px] text-primary font-medium hover:underline"
          >
            <ExternalLink className="h-3 w-3" />
            View in calendar ›
          </button>
          <button
            onClick={() => onNavigateToHeatmap(project.projectId)}
            className="inline-flex items-center gap-1 text-[11px] text-primary font-medium hover:underline"
          >
            <ExternalLink className="h-3 w-3" />
            View in heatmap ›
          </button>
          {project.hasConflict && (
            <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-medium text-destructive">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-destructive" />
              {project.conflictCount} conflict{project.conflictCount !== 1 ? 's' : ''} · Resolve ›
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Row detail drawer ────────────────────────────────────────────────────────

interface RowDetailDrawerProps {
  req: RoleRequirement | null;
  project: ProjectStaffingRow | null;
  open: boolean;
  onClose: () => void;
  onAddResource: (req: RoleRequirement) => void;
  onNavigateToAvailableCapacity: (role: string, from: Date | null, to: Date | null) => void;
  onRemoveResource?: (resourceId: string, projectId: string) => void;
}

function RowDetailDrawer({
  req,
  project,
  open,
  onClose,
  onAddResource,
  onNavigateToAvailableCapacity,
  onRemoveResource,
}: RowDetailDrawerProps) {
  if (!req || !project) return null;

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent
        side="right"
        className="p-0 flex flex-col gap-0 overflow-hidden w-full sm:max-w-[520px]"
      >
        <SheetTitle className="sr-only">
          {req.roleName} — {project.projectName}
        </SheetTitle>
        <SheetDescription className="sr-only">
          Role requirement detail, currently booked resources and actions for{' '}
          {req.roleName} on {project.projectName}.
        </SheetDescription>

        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b shrink-0">
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-sm text-foreground">{req.roleName}</p>
            {req.grade && (
              <p className="text-xs text-muted-foreground mt-0.5">{req.grade}</p>
            )}
            <p className="text-xs text-muted-foreground mt-0.5">{project.projectName}</p>
            <p className="text-xs text-muted-foreground">{project.clientName}</p>
          </div>
          <div className="shrink-0 ml-3">
            <RoleStatusBadge status={req.status} />
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">

          {/* Project details */}
          <div className="px-5 py-4 border-b">
            <p className="text-xs font-semibold text-foreground mb-3">Project details</p>
            <div className="flex flex-col gap-2">
              {[
                { label: 'Project', value: project.projectName },
                { label: 'Client', value: project.clientName },
                { label: 'Start', value: project.startDateStr },
                { label: 'End', value: project.endDateStr },
                { label: 'Staffing', value: '' },
              ].map((row) => row.label === 'Staffing' ? (
                <div key="staffing" className="flex items-center justify-between gap-2">
                  <span className="text-xs text-muted-foreground">Staffing status</span>
                  <StaffingBadge
                    status={project.staffingStatus}
                    unfilledCount={project.unfilledRoleCount}
                  />
                </div>
              ) : (
                <div key={row.label} className="flex items-center justify-between gap-2">
                  <span className="text-xs text-muted-foreground">{row.label}</span>
                  <span className="text-xs font-medium text-foreground text-right">
                    {row.value}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Role requirement */}
          <div className="px-5 py-4 border-b">
            <p className="text-xs font-semibold text-foreground mb-3">Role requirement</p>
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground">Role</span>
                <span className="text-xs font-medium text-foreground">{req.roleName}</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground">Required headcount</span>
                <span className="text-xs font-medium text-foreground">×{req.requiredHeadcount}</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground">Currently booked</span>
                <span className="text-xs font-medium text-foreground">{req.bookedCount}</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground">Gap</span>
                <GapCell req={req} />
              </div>
            </div>
          </div>

          {/* Currently booked resources */}
          <div className="px-5 py-4 border-b">
            <p className="text-xs font-semibold text-foreground mb-3">
              Booked resources ({req.bookedCount})
            </p>
            {req.bookedResources.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No resources currently booked for this role.
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {req.bookedResources.map((r) => (
                  <div
                    key={r.id}
                    className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2.5"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <Avatar className="h-7 w-7 shrink-0">
                        <AvatarImage src={r.photoUrl} alt={r.name} />
                        <AvatarFallback className="text-[10px] font-semibold bg-primary/10 text-primary">
                          {r.initials}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {r.name}
                        </p>
                        <p className="text-xs text-muted-foreground">{r.allocationPct}% allocation</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <BookingTypePill type={r.bookingType} />
                      {onRemoveResource && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-muted-foreground hover:text-destructive"
                          aria-label={`Remove ${r.name} from this role`}
                          onClick={() => onRemoveResource(r.id, req.projectId)}
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* At-risk warning */}
          {project.isAtRisk && (
            <div className="px-5 py-3 bg-warning-500/10 border-b">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-warning-500 shrink-0" />
                <div>
                  <p className="text-xs font-medium text-foreground">
                    At risk — project starts in {project.daysUntilStart} day{project.daysUntilStart !== 1 ? 's' : ''}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    This role has unfilled positions with an imminent project start date.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="px-5 py-4">
            <p className="text-xs font-semibold text-foreground mb-3">Actions</p>
            <div className="flex flex-col gap-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start gap-2 text-xs"
                onClick={() => {
                  onClose();
                  onAddResource(req);
                }}
              >
                <PlusCircle className="h-3.5 w-3.5" />
                Add resource directly ›
              </Button>
              <button
                onClick={() => {
                  onClose();
                  onNavigateToAvailableCapacity(
                    req.roleName,
                    req.projectStartDate,
                    req.projectEndDate,
                  );
                }}
                className="inline-flex items-center gap-1.5 text-[13px] text-primary hover:underline font-medium"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Find available resources ›
              </button>
            </div>
          </div>

        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Empty states ─────────────────────────────────────────────────────────────

function EmptyStateFiltered({ onReset }: { onReset: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
      <div
        className="flex items-center justify-center h-14 w-14 rounded-2xl"
        style={{ background: 'var(--muted)' }}
      >
        <BarChart2 className="h-7 w-7 text-muted-foreground" />
      </div>
      <p className="text-[15px] font-medium text-foreground">
        No projects match your current filters
      </p>
      <p className="text-[13px] text-muted-foreground max-w-xs">
        Try adjusting the filters or date range.
      </p>
      <button
        onClick={onReset}
        className="text-[13px] text-primary hover:underline mt-1 cursor-pointer"
      >
        Reset filters
      </button>
    </div>
  );
}

function EmptyStateAllStaffed({
  onNavigateToForecast,
}: {
  onNavigateToForecast: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
      <div
        className="flex items-center justify-center h-14 w-14 rounded-2xl bg-[var(--accent-bg-outline,#dcfce7)]"
      >
        <CheckCircle2 className="h-7 w-7 text-success-500" />
      </div>
      <p className="text-[15px] font-medium text-foreground">
        All projects are fully staffed
      </p>
      <p className="text-[13px] text-muted-foreground max-w-xs">
        Great work — every project in view has all required roles filled.
      </p>
      <button
        onClick={onNavigateToForecast}
        className="inline-flex items-center gap-1 text-[13px] text-primary hover:underline mt-1 cursor-pointer"
      >
        View utilisation forecast ›
        <ExternalLink className="h-3 w-3" />
      </button>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export interface ProjectStaffingNavigateToCapacityContext {
  projectId?: string;
  roleName?: string;
}

export function ProjectStaffing({
  onNavigate,
  onNavigateToHeatmapWithProject,
  onNavigateToAvailableCapacity: onNavigateToAvailableCapacityExternal,
}: {
  onNavigate?: (page: string) => void;
  /** Called with the project ID when the user clicks "View in heatmap" on a project card */
  onNavigateToHeatmapWithProject?: (projectId: string) => void;
  /** Called when navigating to Available Capacity, carrying role/project context */
  onNavigateToAvailableCapacity?: (context: ProjectStaffingNavigateToCapacityContext) => void;
} = {}) {
  const { canExportReport } = usePermissions();
  const canExport = canExportReport('projectStaffingReport');
  const { isLoading: isContextLoading, refreshResourcesAndGroups } = useScheduler();

  // ── Data mode (useScreenData auto-switches between mockData and liveData)
  const {
    resources: allResources,
    bookings: contextBookings,
    projectDetails,
    clients,
    settings,
    schedulerGroups,
    currentMode,
    isSettingsReady,
    addBooking,
    deleteBooking,
  } = useScreenData('calendar');

  // Scope visible resources by the role's report "View Access" (all / team / own)
  const { scopeResources } = useReportScope('projectStaffingReport');
  const resources = useMemo(() => scopeResources(allResources), [scopeResources, allResources]);

  const { currentCompany, isLoading: isCompanyLoading } = useCompany();
  const companyIdRef = useRef(currentCompany?.id);
  companyIdRef.current = currentCompany?.id;

  // Re-fetch resources and scheduler groups every time the Project Staffing
  // report is activated (i.e. this component mounts).  This ensures that
  // resource changes made externally (e.g. role updates from the Resource
  // Manager app) are reflected immediately without a full browser refresh.
  useEffect(() => {
    refreshResourcesAndGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run only on mount
  }, []);

  // ── Date range state (default: next 12 weeks)
  const { from: defaultFrom, to: defaultTo } = computeDateRange(DEFAULT_DATE_RANGE_WEEKS);
  const [dateFrom, setDateFrom] = useState<Date>(defaultFrom);
  const [dateTo, setDateTo] = useState<Date>(defaultTo);
  const [dateRangeKey, setDateRangeKey] = useState<string>('next12w');

  // ── Live bookings (fetched from API for the report date range)
  // liveData.bookings is intentionally empty — bookings aren't bulk-loaded
  // in live mode, so we fetch them ourselves for the report's date range.
  const [liveReportBookings, setLiveReportBookings] = useState<Booking[]>([]);
  const [hasFetchedBookings, setHasFetchedBookings] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const fetchIdRef = useRef(0);

  const fetchLiveBookings = useCallback(async (start: Date, end: Date) => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    const id = ++fetchIdRef.current;
    try {
      const fetchEnd = addDays(end, 1);
      const apiBookings = await bookingsApi.getByDateRange(
        start.toISOString(),
        fetchEnd.toISOString(),
        false,
        companyIdRef.current,
      );
      if (abortRef.current?.signal.aborted) return;
      if (fetchIdRef.current === id) {
        const mapped = apiBookings.map(mapApiBookingToBooking);
        setLiveReportBookings(mapped);
      }
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      console.error('Failed to fetch live bookings for project staffing report:', err);
      if (fetchIdRef.current === id) {
        setLiveReportBookings([]);
      }
    } finally {
      if (fetchIdRef.current === id) setHasFetchedBookings(true);
    }
  }, []);

  useEffect(() => {
    if (currentMode !== 'live') {
      setHasFetchedBookings(false);
    }
  }, [currentMode]);

  useEffect(() => {
    if (currentMode !== 'live') return;
    if (isCompanyLoading || !currentCompany?.id) return;
    fetchLiveBookings(dateFrom, dateTo);
    return () => { abortRef.current?.abort(); };
  }, [currentMode, dateFrom, dateTo, fetchLiveBookings, currentCompany?.id, isCompanyLoading]);

  const bookings = currentMode === 'live' ? liveReportBookings : contextBookings;

  // True while bookings are still loading in live mode.
  const isLiveDataLoading = currentMode === 'live' && (isContextLoading || !hasFetchedBookings);

  // ── Filter state
  const [selectedStatuses, setSelectedStatuses] = useState<ProjectStatus[]>([
    'active',
    'pipeline',
  ]);
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

  // ── Pending (draft) filter state for the filter drawer
  const [pendingStatuses, setPendingStatuses] = useState<ProjectStatus[]>([
    'active',
    'pipeline',
  ]);
  const [pendingProjects, setPendingProjects] = useState<string[]>([]);
  const [pendingClients, setPendingClients] = useState<string[]>([]);
  const [pendingDepartments, setPendingDepartments] = useState<string[]>([]);
  const [pendingTeams, setPendingTeams] = useState<string[]>([]);
  const [pendingRoles, setPendingRoles] = useState<string[]>([]);
  const [staffingStatusFilter, setStaffingStatusFilter] =
    useState<StaffingStatusFilter>('all');

  // ── View state
  const [viewMode, setViewMode] = useState<ViewMode>('card');

  // ── Sort (list view)
  const [sortCol, setSortCol] = useState<string>('project');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // ── Column visibility
  const [colConfigOpen, setColConfigOpen] = useState(false);
  type StaffingColKey = 'client' | 'role' | 'grade' | 'required' | 'booked' | 'gap' | 'startDate' | 'endDate' | 'bookingType' | 'status' | 'bookedResources';
  const ALL_STAFFING_COLS: { key: StaffingColKey; label: string; description: string }[] = [
    { key: 'client',          label: 'Client',           description: 'Client associated with the project'    },
    { key: 'role',            label: 'Role',             description: 'Required role for the project'          },
    { key: 'grade',           label: 'Grade',            description: 'Required grade or level'                },
    { key: 'required',        label: 'Required',         description: 'Number of headcount required'           },
    { key: 'booked',          label: 'Booked',           description: 'Number of resources booked'             },
    { key: 'gap',             label: 'Gap',              description: 'Staffing gap indicator'                 },
    { key: 'startDate',       label: 'Start date',       description: 'Project start date'                     },
    { key: 'endDate',         label: 'End date',         description: 'Project end date'                       },
    { key: 'bookingType',     label: 'Booking type',     description: 'Type of booking (confirmed / planned)' },
    { key: 'status',          label: 'Status',           description: 'Staffing status for this role'         },
    { key: 'bookedResources', label: 'Booked resources', description: 'Resources assigned to this role'       },
  ];
  const DEFAULT_STAFFING_COLS = new Set<StaffingColKey>(['client','role','grade','required','booked','gap','startDate','endDate','bookingType','status','bookedResources']);
  const [visibleStaffingCols, setVisibleStaffingCols] = useState<Set<StaffingColKey>>(new Set(DEFAULT_STAFFING_COLS));
  const toggleStaffingCol = (key: StaffingColKey) => setVisibleStaffingCols(prev => {
    const next = new Set(prev);
    if (next.has(key)) { if (next.size > 1) next.delete(key); } else next.add(key);
    return next;
  });
  const resetStaffingCols = () => setVisibleStaffingCols(new Set(DEFAULT_STAFFING_COLS));

  // ── Pagination (list view)
  type PageSizeOption = 25 | 50 | 'all';
  const [pageSize, setPageSize] = useState<PageSizeOption>(25);
  const [currentListPage, setCurrentListPage] = useState(1);

  // ── Drawer state (list view row detail)
  const [drawerReq, setDrawerReq] = useState<RoleRequirement | null>(null);
  const [drawerProject, setDrawerProject] = useState<ProjectStaffingRow | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // ── Filter drawer
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const handleFilterDrawerOpen = useCallback(() => {
    setPendingStatuses(selectedStatuses);
    setPendingProjects(selectedProjects);
    setPendingClients(selectedClients);
    setPendingDepartments(selectedDepartments);
    setPendingTeams(selectedTeams);
    setPendingRoles(selectedRoles);
    setFilterDrawerOpen(true);
  }, [selectedStatuses, selectedProjects, selectedClients, selectedDepartments, selectedTeams, selectedRoles]);

  const handleApplyFilters = useCallback(() => {
    setSelectedStatuses(pendingStatuses);
    setSelectedProjects(pendingProjects);
    setSelectedClients(pendingClients);
    setSelectedDepartments(pendingDepartments);
    setSelectedTeams(pendingTeams);
    setSelectedRoles(pendingRoles);
    setFilterDrawerOpen(false);
  }, [pendingStatuses, pendingProjects, pendingClients, pendingDepartments, pendingTeams, pendingRoles]);

  // ── Booking modal (Add Resource)
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [bookingModalProjectName, setBookingModalProjectName] = useState<string>('');
  const [bookingModalReq, setBookingModalReq] = useState<RoleRequirement | null>(null);

  // ── Highlighted project (deep-link)
  const [highlightedProjectId, setHighlightedProjectId] = useState<string | null>(null);

  // ── Card stat filter state
  const [activeStatFilter, setActiveStatFilter] = useState<
    'gaps' | 'at-risk' | null
  >(null);

  // ── Data
  const effectiveStaffingFilter: StaffingStatusFilter =
    activeStatFilter === 'gaps' ? 'gaps'
    : staffingStatusFilter;

  const data = useProjectStaffingData(
    dateFrom,
    dateTo,
    selectedStatuses,
    selectedProjects,
    selectedClients,
    selectedDepartments,
    selectedTeams,
    selectedRoles,
    effectiveStaffingFilter,
    bookings,
  );

  // Filter to at-risk projects when stat card clicked
  const displayedProjects = useMemo(() => {
    if (activeStatFilter === 'at-risk') {
      return data.projects.filter((p) => p.isAtRisk);
    }
    return data.projects;
  }, [data.projects, activeStatFilter]);

  // ── Filter options ─────────────────────────────────────────────────────
  const projectOptions = useMemo(
    () =>
      projectDetails
        .filter((p) => !p.isDeleted)
        .map((p) => ({
          value: String(p.id),
          label: p.name,
        })),
    [projectDetails],
  );

  const clientOptions = useMemo(() => {
    const seen = new Set<string>();
    return projectDetails
      .filter((p) => !p.isDeleted)
      .reduce<Array<{ value: string; label: string }>>((acc, p) => {
        const name = p.clientName ?? '—';
        if (name && name !== '—' && !seen.has(name)) {
          seen.add(name);
          acc.push({ value: name, label: name });
        }
        return acc;
      }, [])
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [projectDetails]);

  const departmentOptions = useMemo(() => {
    const seen = new Set<string>();
    return resources
      .filter((r) => r.id !== 'unallocated')
      .reduce<Array<{ value: string; label: string }>>((acc, r) => {
        const dept = r.department ?? '';
        if (dept && !seen.has(dept)) {
          seen.add(dept);
          acc.push({ value: dept, label: dept });
        }
        return acc;
      }, [])
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [resources]);

  const roleOptions = useMemo(() => {
    const seen = new Set<string>();
    return resources
      .filter((r) => r.id !== 'unallocated')
      .reduce<Array<{ value: string; label: string }>>((acc, r) => {
        const role = r.role ?? '';
        if (role && !seen.has(role)) {
          seen.add(role);
          acc.push({ value: role, label: role });
        }
        return acc;
      }, [])
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [resources]);

  const teamsEnabled =
    settings.groupingMethod === 'scheduler-group' && schedulerGroups.length > 0;

  // Which grouping filter to render: `null` until company settings and
  // scheduler groups have resolved, so an unhydrated company never falls back
  // to showing Department.
  const groupingFilter: 'team' | 'department' | null = !isSettingsReady
    ? null
    : teamsEnabled
      ? 'team'
      : 'department';

  const teamOptions = useMemo(
    () =>
      schedulerGroups
        .map((g) => ({ value: g.name, label: g.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [schedulerGroups],
  );

  // Only the dimension matching the configured grouping method is filterable,
  // so any selection made against the other one is dropped.
  useEffect(() => {
    if (!isSettingsReady) return;
    if (teamsEnabled) {
      setSelectedDepartments([]);
      setPendingDepartments([]);
    } else {
      setSelectedTeams([]);
      setPendingTeams([]);
    }
  }, [teamsEnabled, isSettingsReady]);

  // ── Date range preset handler
  const handleDateRangeChange = useCallback((key: string) => {
    setDateRangeKey(key);
    const weeks =
      key === 'next4w'
        ? 4
        : key === 'next8w'
        ? 8
        : key === 'next12w'
        ? 12
        : key === 'next26w'
        ? 26
        : 12;
    const { from, to } = computeDateRange(weeks);
    setDateFrom(from);
    setDateTo(to);
  }, []);

  // ── Sort handler
  const handleSort = useCallback(
    (col: string) => {
      if (col === sortCol) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortCol(col);
        setSortDir('asc');
      }
    },
    [sortCol],
  );

  // ── Sorted flat rows (list view)
  const sortedFlatRows = useMemo(() => {
    const rows = activeStatFilter === 'at-risk'
      ? data.flatRows.filter((r) => r.project.isAtRisk)
      : data.flatRows;

    return [...rows].sort((a, b) => {
      let va: string | number = '';
      let vb: string | number = '';
      switch (sortCol) {
        case 'project':    va = a.project.projectName; vb = b.project.projectName; break;
        case 'client':     va = a.project.clientName;  vb = b.project.clientName;  break;
        case 'role':       va = a.req.roleName;         vb = b.req.roleName;         break;
        case 'grade':      va = a.req.grade;            vb = b.req.grade;            break;
        case 'required':   va = a.req.requiredHeadcount; vb = b.req.requiredHeadcount; break;
        case 'booked':     va = a.req.bookedCount;      vb = b.req.bookedCount;      break;
        case 'gap':        va = a.req.gapCount;         vb = b.req.gapCount;         break;
        case 'start':
          va = a.project.projectStartDate?.getTime() ?? 0;
          vb = b.project.projectStartDate?.getTime() ?? 0;
          break;
        case 'end':
          va = a.project.projectEndDate?.getTime() ?? 0;
          vb = b.project.projectEndDate?.getTime() ?? 0;
          break;
        case 'bookedResources':
          va = a.req.bookedResources.map((r) => r.name).join(', ');
          vb = b.req.bookedResources.map((r) => r.name).join(', ');
          break;
        case 'status':     va = a.req.status;           vb = b.req.status;           break;
        default:           va = a.project.projectName; vb = b.project.projectName;
      }
      if (typeof va === 'string') {
        return sortDir === 'asc'
          ? va.localeCompare(vb as string)
          : (vb as string).localeCompare(va);
      }
      return sortDir === 'asc'
        ? (va as number) - (vb as number)
        : (vb as number) - (va as number);
    });
  }, [data.flatRows, sortCol, sortDir, activeStatFilter]);

  // ── Paginated rows (list view)
  const totalListRows = sortedFlatRows.length;
  const totalListPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(totalListRows / pageSize));
  const safeListPage = Math.min(currentListPage, totalListPages);
  const paginatedFlatRows = useMemo(() => {
    if (pageSize === 'all') return sortedFlatRows;
    const start = (safeListPage - 1) * pageSize;
    return sortedFlatRows.slice(start, start + pageSize);
  }, [sortedFlatRows, pageSize, safeListPage]);

  // Reset to page 1 when sort/filter/data changes
  useEffect(() => {
    setCurrentListPage(1);
  }, [sortCol, sortDir, activeStatFilter, staffingStatusFilter, selectedStatuses, selectedProjects, selectedClients, selectedDepartments, selectedTeams, selectedRoles, dateFrom, dateTo]);

  // ── Reset all filters
  const handleReset = useCallback(() => {
    setSelectedStatuses(['active', 'pipeline']);
    setSelectedProjects([]);
    setSelectedClients([]);
    setSelectedDepartments([]);
    setSelectedTeams([]);
    setSelectedRoles([]);
    setPendingStatuses(['active', 'pipeline']);
    setPendingProjects([]);
    setPendingClients([]);
    setPendingDepartments([]);
    setPendingTeams([]);
    setPendingRoles([]);
    setStaffingStatusFilter('all');
    setActiveStatFilter(null);
    setDateRangeKey('next12w');
    const { from, to } = computeDateRange(DEFAULT_DATE_RANGE_WEEKS);
    setDateFrom(from);
    setDateTo(to);
    setSortCol('project');
    setSortDir('asc');
  }, []);

  // ── Add resource (open booking modal)
  const handleAddResource = useCallback((req: RoleRequirement) => {
    setBookingModalProjectName(req.projectName);
    setBookingModalReq(req);
    setBookingModalOpen(true);
  }, []);

  // ── Find available resource (card view)
  const handleFindAvailableFromCard = useCallback(
    (req: RoleRequirement) => {
      onNavigateToAvailableCapacityExternal?.({ projectId: req.projectId, roleName: req.roleName });
      onNavigate?.('report-available-capacity');
    },
    [onNavigate, onNavigateToAvailableCapacityExternal],
  );

  // ── Remove resource from project (soft-delete bookings for resource+project)
  const handleRemoveResource = useCallback(
    async (resourceId: string, projectId: string) => {
      const resourceBookings = bookings.filter(
        (b) =>
          b.resourceId === resourceId &&
          (String(b.projectId ?? '') === projectId || b.projectName === projectId) &&
          b.status !== 'cancelled',
      );
      if (resourceBookings.length === 0) {
        toast.error('No bookings found for this resource on this project.');
        return;
      }
      try {
        for (const b of resourceBookings) {
          await deleteBooking(String(b.id));
        }
        toast.success('Resource removed', {
          description: `${resourceBookings.length} booking${resourceBookings.length !== 1 ? 's' : ''} removed.`,
        });
        if (currentMode === 'live') {
          fetchLiveBookings(dateFrom, dateTo);
        }
      } catch {
        toast.error('Failed to remove resource bookings.');
      }
    },
    [bookings, deleteBooking, currentMode, fetchLiveBookings, dateFrom, dateTo],
  );

  // ── Navigate to calendar
  const handleNavigateToCalendar = useCallback(
    (_projectId: string) => {
      onNavigate?.('calendar');
    },
    [onNavigate],
  );

  // ── Navigate to heatmap — forwards project ID so the heatmap opens pre-filtered
  const handleNavigateToHeatmap = useCallback(
    (projectId: string) => {
      onNavigateToHeatmapWithProject?.(projectId);
      onNavigate?.('heatmap');
    },
    [onNavigate, onNavigateToHeatmapWithProject],
  );

  // ── Navigate to available capacity
  const handleNavigateToAvailableCapacity = useCallback(
    (role: string, _from: Date | null, _to: Date | null) => {
      // Pass role/project context so AvailableCapacity can pre-populate the booking form
      // and know to return to Project Staffing after saving.
      // We store the current drawerReq's projectId if available.
      const projectId = drawerReq?.projectId;
      onNavigateToAvailableCapacityExternal?.({ projectId, roleName: role });
      onNavigate?.('report-available-capacity');
    },
    [onNavigate, onNavigateToAvailableCapacityExternal, drawerReq],
  );

  // ── Export to Excel
  const handleExportExcel = useCallback(() => {
    if (data.flatRows.length === 0) {
      toast.error('Nothing to export', {
        description: 'No data matches the current filters.',
      });
      return;
    }

    const dateLabel = formatDateRange(dateFrom, dateTo);

    const rows = (viewMode === 'list' ? sortedFlatRows : data.flatRows).map(({ req, project }) => ({
      Project: project.projectName,
      Client: project.clientName,
      'Project Start': project.startDateStr,
      'Project End': project.endDateStr,
      'Staffing Status':
        project.staffingStatus.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      Role: req.roleName,
      Grade: req.grade || '—',
      Required: req.requiredHeadcount,
      Booked: req.bookedCount,
      Gap: req.gapCount > 0 ? `${req.bookedCount} of ${req.requiredHeadcount} filled` : 'Filled',
      'Booking Type':
        req.bookingTypeSummary.charAt(0).toUpperCase() + req.bookingTypeSummary.slice(1),
      'Role Status':
        req.status.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      'Booked Resources': req.bookedResources.map((r) => r.name).join(', ') || '—',
      Period: dateLabel,
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = Object.keys(rows[0] ?? {}).map((k) => ({
      wch: Math.max(k.length, ...rows.map((r) => String(r[k as keyof typeof r]).length)) + 2,
    }));
    XLSX.utils.book_append_sheet(wb, ws, 'Project Staffing');

    const filename = `project-staffing-${format(dateFrom, 'yyyy-MM-dd')}.xlsx`;
    XLSX.writeFile(wb, filename);

    toast.success('Export complete', {
      description: `${rows.length} row${rows.length !== 1 ? 's' : ''} exported to Excel.`,
    });
  }, [data.flatRows, sortedFlatRows, dateFrom, dateTo, viewMode]);

  // ── Has any active filter (to show reset link)
  const hasActiveFilters =
    selectedStatuses.length !== 2 ||
    !selectedStatuses.includes('active') ||
    !selectedStatuses.includes('pipeline') ||
    selectedProjects.length > 0 ||
    selectedClients.length > 0 ||
    selectedDepartments.length > 0 ||
    selectedTeams.length > 0 ||
    selectedRoles.length > 0 ||
    staffingStatusFilter !== 'all' ||
    activeStatFilter !== null ||
    dateRangeKey !== 'next12w';

  // ── Status multi-select options
  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'pipeline', label: 'Pipeline' },
    { value: 'on-hold', label: 'On hold' },
    { value: 'completed', label: 'Completed' },
  ];

  const { ref: contentRef, gutter: contentGutter } = useScrollbarGutter<HTMLDivElement>();
  const gutterPadding = { paddingRight: `calc(1.5rem + ${contentGutter}px)` };

  return (
    <div className="flex flex-col h-full overflow-hidden bg-background">

      {/* ── Page header ──────────────────────────────────────────────────── */}
      <div
        className="flex items-center justify-between px-6 py-4 shrink-0"
        style={gutterPadding}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 shrink-0">
            <BarChart2 className="h-4 w-4 text-primary" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-foreground leading-tight">
              Project staffing
            </h1>
            <p className="text-[13px] text-muted-foreground mt-0.5">
              Resource requirements vs confirmed bookings per project — gaps and overstaffing
              highlighted
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0 ml-4">
          <FeatureFeedbackCollector feature="Project Staffing" />
          <HelpButton />
        </div>
      </div>

      {/* ── Filters bar ──────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 px-6 py-3 shrink-0" style={gutterPadding}>
        {/* Date range */}
        <Select value={dateRangeKey} onValueChange={handleDateRangeChange}>
          <SelectTrigger className="h-8 w-auto min-w-[130px] shrink-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="next4w">Next 4 weeks</SelectItem>
            <SelectItem value="next8w">Next 8 weeks</SelectItem>
            <SelectItem value="next12w">Next 12 weeks</SelectItem>
            <SelectItem value="next26w">Next 26 weeks</SelectItem>
          </SelectContent>
        </Select>

        {/* Date range display — read-only */}
        <div
          className="hidden sm:inline-flex items-center h-8 px-3 rounded-md border border-input text-sm text-muted-foreground shrink-0"
        >
          {formatDateRange(dateFrom, dateTo)}
        </div>

        {/* Staffing status filter */}
        <Select
          value={activeStatFilter === 'gaps' ? 'gaps' : staffingStatusFilter}
          onValueChange={(v) => {
            setStaffingStatusFilter(v as StaffingStatusFilter);
            setActiveStatFilter(null);
          }}
        >
          <SelectTrigger className="h-8 w-auto min-w-[140px] shrink-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All staffing</SelectItem>
            <SelectItem value="gaps">Gaps only</SelectItem>
            <SelectItem value="fully-staffed">Fully staffed</SelectItem>
            <SelectItem value="overstaffed">Overstaffed</SelectItem>
          </SelectContent>
        </Select>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Reset link — only when secondary filters are active */}
        {hasActiveFilters && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1"
            onClick={handleReset}
          >
            <X className="h-4 w-4" />
            Reset
          </Button>
        )}

        {/* Filter button */}
        <Button
          variant={hasActiveFilters ? 'default' : 'outline'}
          size="sm"
          className="relative h-8 w-8 p-0 shrink-0"
          onClick={handleFilterDrawerOpen}
          aria-label="Open filters"
        >
          <Filter className="h-4 w-4" />
          {(() => {
            const count =
              (selectedStatuses.length !== 2 || !selectedStatuses.includes('active') || !selectedStatuses.includes('pipeline') ? 1 : 0) +
              (selectedProjects.length > 0 ? 1 : 0) +
              (selectedClients.length > 0 ? 1 : 0) +
              (selectedDepartments.length > 0 ? 1 : 0) +
              (selectedTeams.length > 0 ? 1 : 0) +
              (selectedRoles.length > 0 ? 1 : 0);
            return count > 0 ? (
              <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground leading-none">
                {count}
              </span>
            ) : null;
          })()}
        </Button>

        {/* Column config button */}
        <Button
          variant="outline"
          size="sm"
          className="relative h-8 w-8 p-0 shrink-0"
          onClick={() => setColConfigOpen(true)}
          aria-label="Configure columns"
        >
          <SlidersHorizontal className="h-4 w-4" />
          {visibleStaffingCols.size < ALL_STAFFING_COLS.length && (
            <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground leading-none">
              {ALL_STAFFING_COLS.length - visibleStaffingCols.size}
            </span>
          )}
        </Button>

        {/* Export to Excel */}
        {canExport && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0 shrink-0"
                onClick={handleExportExcel}
                aria-label="Export project staffing report to Excel"
              >
                <Download className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Export to Excel</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        )}
      </div>

      {/* ── Column config sheet ─────────────────────────────────────────── */}
      <Sheet open={colConfigOpen} onOpenChange={setColConfigOpen}>
        <SheetContent side="right" className="w-[400px] sm:max-w-[400px] flex flex-col p-0 gap-0">
          <SheetTitle className="sr-only">Configure columns</SheetTitle>
          <SheetDescription className="sr-only">Toggle columns on or off for the project staffing table.</SheetDescription>
          <SheetHeader className="px-6 pt-6 pb-5 border-b shrink-0">
            <div className="flex items-center gap-3 pr-8">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 shrink-0">
                <Settings2 className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-base font-semibold leading-tight">Customise Columns</p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">Toggle columns on or off to personalise the table.</p>
              </div>
            </div>
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold tabular-nums shrink-0">{visibleStaffingCols.size}</div>
                <span className="text-sm text-muted-foreground">of {ALL_STAFFING_COLS.length} columns showing</span>
              </div>
              <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-foreground" onClick={resetStaffingCols}>
                <RotateCcw className="h-3.5 w-3.5" />Reset defaults
              </Button>
            </div>
          </SheetHeader>
          <ScrollArea className="flex-1 min-h-0">
            <ScrollAreaViewport>
              <div className="px-4 py-4 space-y-2">
                {ALL_STAFFING_COLS.map((col) => {
                  const isVisible = visibleStaffingCols.has(col.key);
                  return (
                    <div key={col.key} className={['flex items-center gap-3 px-4 py-3.5 rounded-xl border transition-colors duration-150', isVisible ? 'bg-card border-border hover:border-border/80 hover:bg-accent/30' : 'bg-muted/10 border-border/30'].join(' ')}>
                      <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground/30" />
                      <div className="flex-1 min-w-0">
                        <p className={['text-sm font-medium leading-snug', !isVisible ? 'text-muted-foreground' : 'text-foreground'].join(' ')}>{col.label}</p>
                        <p className="text-xs text-muted-foreground/60 mt-0.5 truncate leading-tight">{col.description}</p>
                      </div>
                      <Switch checked={isVisible} onCheckedChange={() => toggleStaffingCol(col.key)} aria-label={`${isVisible ? 'Hide' : 'Show'} ${col.label} column`} />
                    </div>
                  );
                })}
              </div>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>
          <SheetFooter className="px-4 py-3 border-t shrink-0">
            <Button className="w-full" onClick={() => setColConfigOpen(false)}>Done</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* ── Filter drawer ─────────────────────────────────────────────────── */}
      <Sheet open={filterDrawerOpen} onOpenChange={setFilterDrawerOpen}>
        <SheetContent side="right" className="w-[380px] sm:max-w-[380px] flex flex-col p-0 gap-0">
          <SheetTitle className="sr-only">Project staffing filters</SheetTitle>
          <SheetDescription className="sr-only">
            Filter projects by status, client, department, role and more.
          </SheetDescription>

          {/* Drawer header */}
          <SheetHeader className="px-4 pt-4 pb-3 border-b shrink-0 gap-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold">Filters</span>
              {(() => {
                const count =
                  (pendingStatuses.length !== 2 || !pendingStatuses.includes('active') || !pendingStatuses.includes('pipeline') ? 1 : 0) +
                  (pendingProjects.length > 0 ? 1 : 0) +
                  (pendingClients.length > 0 ? 1 : 0) +
                  (pendingDepartments.length > 0 ? 1 : 0) +
                  (pendingRoles.length > 0 ? 1 : 0);
                return (
                  <Badge variant="secondary" className="flex items-center gap-1 px-2.5 py-0.5 h-6 text-[11px] font-medium rounded-full bg-primary/10 text-primary border-0 hover:bg-primary/10">
                    <Filter className="h-3 w-3" />
                    {count} {count === 1 ? 'filter' : 'filters'} active
                  </Badge>
                );
              })()}
            </div>
          </SheetHeader>

          {/* Drawer body */}
          <ScrollArea className="flex-1">
            <ScrollAreaViewport>
              <div className="px-4 py-4 space-y-4">

                {/* Project status */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Project status</label>
                  <MultiSelect
                    options={statusOptions}
                    placeholder="All statuses"
                    value={pendingStatuses}
                    onValueChange={(v) => setPendingStatuses(v as ProjectStatus[])}
                    maxDisplay={3}
                  />
                </div>

                <Separator />

                {/* Projects */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Project</label>
                  <MultiSelect
                    options={projectOptions}
                    placeholder="All projects"
                    value={pendingProjects}
                    onValueChange={setPendingProjects}
                    maxDisplay={2}
                  />
                </div>

                <Separator />

                {/* Clients */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Client</label>
                  <MultiSelect
                    options={clientOptions}
                    placeholder="All clients"
                    value={pendingClients}
                    onValueChange={setPendingClients}
                    maxDisplay={2}
                  />
                </div>

                <Separator />

                {/* Grouping dimension — Planner Group or Department per general settings */}
                {groupingFilter === 'team' ? (
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-foreground">Planner Group</label>
                    <MultiSelect
                      options={teamOptions}
                      placeholder="All planner groups"
                      value={pendingTeams}
                      onValueChange={setPendingTeams}
                      maxDisplay={2}
                    />
                  </div>
                ) : groupingFilter === 'department' ? (
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-foreground">Department</label>
                    <MultiSelect
                      options={departmentOptions}
                      placeholder="All departments"
                      value={pendingDepartments}
                      onValueChange={setPendingDepartments}
                      maxDisplay={2}
                    />
                  </div>
                ) : null}

                <Separator />

                {/* Roles */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Role</label>
                  <MultiSelect
                    options={roleOptions}
                    placeholder="All roles"
                    value={pendingRoles}
                    onValueChange={setPendingRoles}
                    maxDisplay={2}
                  />
                </div>
              </div>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>

          {/* Drawer footer */}
          <SheetFooter className="px-4 py-3 border-t shrink-0 flex-row items-center justify-between gap-2">
            <Button
              variant="ghost"
              className="h-9 gap-1.5 text-muted-foreground hover:text-foreground px-2"
              onClick={() => {
                setPendingStatuses(['active', 'pipeline']);
                setPendingProjects([]);
                setPendingClients([]);
                setPendingDepartments([]);
                setPendingRoles([]);
                setSelectedStatuses(['active', 'pipeline']);
                setSelectedProjects([]);
                setSelectedClients([]);
                setSelectedDepartments([]);
                setSelectedRoles([]);
                setFilterDrawerOpen(false);
              }}
              disabled={
                pendingStatuses.length === 2 &&
                pendingStatuses.includes('active') &&
                pendingStatuses.includes('pipeline') &&
                pendingProjects.length === 0 &&
                pendingClients.length === 0 &&
                pendingDepartments.length === 0 &&
                pendingRoles.length === 0
              }
            >
              <X className="h-3.5 w-3.5" />
              Clear All
            </Button>
            <Button className="h-9 gap-1.5 px-5" onClick={handleApplyFilters}>
              <Check className="h-3.5 w-3.5" />
              Apply Filters
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* ── Scrollable body ───────────────────────────────────────────────── */}
      <div ref={contentRef} className="flex-1 overflow-y-auto">
        <div className="p-6 flex flex-col gap-6">

          {/* ── Summary stat cards (PRD §5.4) ────────────────────────────── */}
          {!isLiveDataLoading && <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCard
              value={data.summary.totalProjects}
              label="Total projects"
              subLabel="Matching current filters"
              valueColour="default"
            />
            <StatCard
              value={data.summary.rolesWithGaps}
              label="Roles with gaps"
              subLabel="Role requirements with gap > 0"
              valueColour={data.summary.rolesWithGaps > 0 ? 'destructive' : 'default'}
            />
            <StatCard
              value={data.summary.atRiskProjects}
              label="At risk"
              subLabel={`Start within ${AT_RISK_WEEKS}w · unfilled roles`}
              valueColour={data.summary.atRiskProjects > 0 ? 'warning' : 'default'}
              isFilterable
              isActive={activeStatFilter === 'at-risk'}
              onClick={() =>
                setActiveStatFilter((v) => (v === 'at-risk' ? null : 'at-risk'))
              }
            />
            <StatCard
              value={data.summary.fullyStaffedRoles}
              label="Fully staffed"
              subLabel="Role requirements fully filled"
              valueColour={data.summary.fullyStaffedRoles > 0 ? 'teal' : 'default'}
            />
          </div>}

          {/* ── View toggle + results summary ────────────────────────────── */}
          {!isLiveDataLoading && !data.isEmpty && (
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-[13px] text-muted-foreground">
                {displayedProjects.length} project
                {displayedProjects.length !== 1 ? 's' : ''}
                {activeStatFilter === 'at-risk' ? ' at risk' : ''}
              </p>
              <div className="flex items-center gap-3">
                {hasActiveFilters && (
                  <button
                    onClick={handleReset}
                    className="text-[12px] text-muted-foreground hover:text-foreground hover:underline"
                  >
                    Reset filters
                  </button>
                )}
                <Segmented
                  options={[
                    { label: 'Card view', value: 'card' as ViewMode, icon: <LayoutGrid className="h-3 w-3" /> },
                    { label: 'List view', value: 'list' as ViewMode, icon: <List className="h-3 w-3" /> },
                  ]}
                  value={viewMode}
                  onChange={setViewMode}
                />
              </div>
            </div>
          )}

          {/* ── Content area ──────────────────────────────────────────────── */}
          {isLiveDataLoading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Loading project staffing data…</p>
            </div>
          ) : data.isEmpty ? (
            data.allFullyStaffed ? (
              <EmptyStateAllStaffed
                onNavigateToForecast={() => onNavigate?.('report-utilisation-forecast')}
              />
            ) : (
              <EmptyStateFiltered onReset={handleReset} />
            )
          ) : viewMode === 'card' ? (

            /* ── Card view ─────────────────────────────────────────────── */
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {displayedProjects.map((project) => (
                <ProjectCard
                  key={project.projectId}
                  project={project}
                  highlighted={highlightedProjectId === project.projectId}
                  onAddResource={handleAddResource}
                  onFindAvailable={handleFindAvailableFromCard}
                  onNavigateToCalendar={handleNavigateToCalendar}
                  onNavigateToHeatmap={handleNavigateToHeatmap}
                />
              ))}
            </div>

          ) : (

            /* ── List view ─────────────────────────────────────────────── */
            <div className={RAW_TABLE_CONTAINER_CLASS}>
              <div className={RAW_TABLE_SCROLL_CLASS}>
                <table className={RAW_TABLE_CLASS}>
                  <thead>
                    <tr className={TABLE_HEADER_ROW_CLASS}>
                      <SortHeader label="Project" col="project" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} minWidth={160} />
                      {visibleStaffingCols.has('client') && <SortHeader label="Client" col="client" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} minWidth={130} />}
                      {visibleStaffingCols.has('startDate') && <SortHeader label="Start date" col="start" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} minWidth={110} />}
                      {visibleStaffingCols.has('endDate') && <SortHeader label="End date" col="end" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} minWidth={110} />}
                      {visibleStaffingCols.has('role') && <SortHeader label="Role" col="role" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} minWidth={130} />}
                      {visibleStaffingCols.has('grade') && <SortHeader label="Grade" col="grade" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} minWidth={90} />}
                      {visibleStaffingCols.has('required') && <SortHeader label="Required" col="required" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" minWidth={80} />}
                      {visibleStaffingCols.has('booked') && <SortHeader label="Booked" col="booked" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" minWidth={80} />}
                      {visibleStaffingCols.has('gap') && <SortHeader label="Gap" col="gap" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} minWidth={130} />}
                      {visibleStaffingCols.has('bookingType') && <SortHeader label="Booking type" col="type" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} minWidth={110} />}
                      {visibleStaffingCols.has('status') && <SortHeader label="Status" col="status" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} minWidth={130} />}
                      {visibleStaffingCols.has('bookedResources') && <SortHeader label="Booked resources" col="bookedResources" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} minWidth={150} />}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedFlatRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={12}
                          className="px-4 py-12 text-center text-[13px] text-muted-foreground"
                        >
                          No role requirements match the current filters.
                        </td>
                      </tr>
                    ) : (
                      paginatedFlatRows.map(({ key, req, project }) => (
                        <tr
                          key={key}
                          className={`${TABLE_ROW_CLASS} cursor-pointer`}
                          onClick={() => {
                            setDrawerReq(req);
                            setDrawerProject(project);
                            setDrawerOpen(true);
                          }}
                        >
                          {/* Project */}
                          <td className="px-3 py-2.5 align-middle">
                            <div className="flex items-center gap-1.5">
                              <span className="font-medium text-foreground truncate max-w-[160px]">
                                {project.projectName}
                              </span>
                              {project.isAtRisk && (
                                <AlertTriangle className="h-3 w-3 text-warning-500 shrink-0" />
                              )}
                            </div>
                          </td>
                          {/* Client */}
                          {visibleStaffingCols.has('client') && <td className="px-3 py-2.5 align-middle text-muted-foreground">{project.clientName}</td>}
                          {/* Start date */}
                          {visibleStaffingCols.has('startDate') && <td className="px-3 py-2.5 align-middle text-muted-foreground whitespace-nowrap">{project.startDateStr}</td>}
                          {/* End date */}
                          {visibleStaffingCols.has('endDate') && <td className="px-3 py-2.5 align-middle text-muted-foreground whitespace-nowrap">{project.endDateStr}</td>}
                          {/* Role */}
                          {visibleStaffingCols.has('role') && <td className="px-3 py-2.5 align-middle font-medium text-foreground">{req.roleName}</td>}
                          {/* Grade */}
                          {visibleStaffingCols.has('grade') && <td className="px-3 py-2.5 align-middle text-muted-foreground">{req.grade || '—'}</td>}
                          {/* Required */}
                          {visibleStaffingCols.has('required') && <td className="px-3 py-2.5 align-middle text-right text-foreground">{req.requiredHeadcount}</td>}
                          {/* Booked */}
                          {visibleStaffingCols.has('booked') && <td className="px-3 py-2.5 align-middle text-right text-foreground">{req.bookedCount}</td>}
                          {/* Gap */}
                          {visibleStaffingCols.has('gap') && <td className="px-3 py-2.5 align-middle"><GapCell req={req} /></td>}
                          {/* Booking type */}
                          {visibleStaffingCols.has('bookingType') && <td className="px-3 py-2.5 align-middle"><BookingTypePill type={req.bookingTypeSummary} /></td>}
                          {/* Status */}
                          {visibleStaffingCols.has('status') && <td className="px-3 py-2.5 align-middle"><RoleStatusBadge status={req.status} /></td>}
                          {/* Booked resources */}
                          {visibleStaffingCols.has('bookedResources') && (
                            <td className="px-3 py-2.5 align-middle">
                              {req.bookedResources.length > 0 ? (
                                <div className="flex items-center gap-1.5">
                                  <div className="flex -space-x-1">
                                    {req.bookedResources.slice(0, 3).map((r) => (
                                      <Avatar key={r.id} className="h-5 w-5 border border-background shrink-0">
                                        <AvatarImage src={r.photoUrl} alt={r.name} />
                                        <AvatarFallback className="text-[8px] font-semibold bg-primary/10 text-primary">
                                          {r.initials}
                                        </AvatarFallback>
                                      </Avatar>
                                    ))}
                                    {req.bookedResources.length > 3 && (
                                      <span className="inline-flex items-center justify-center h-5 w-5 rounded-full border border-background bg-muted text-[8px] font-medium text-muted-foreground">
                                        +{req.bookedResources.length - 3}
                                      </span>
                                    )}
                                  </div>
                                  <span className="text-[11px] text-muted-foreground truncate max-w-[120px]">
                                    {req.bookedResources.map((r) => r.name).join(', ')}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-[11px] text-muted-foreground">—</span>
                              )}
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination controls */}
              {sortedFlatRows.length > 0 && (
                <div className="flex items-center justify-between px-4 py-2.5 border-t border-border bg-muted/30">
                  <div className="flex items-center gap-2">
                    <span className="text-[12px] text-muted-foreground">Rows per page</span>
                    <Select
                      value={String(pageSize)}
                      onValueChange={(v) => {
                        setPageSize(v === 'all' ? 'all' : (Number(v) as 25 | 50));
                        setCurrentListPage(1);
                      }}
                    >
                      <SelectTrigger className="h-7 text-[12px] w-auto min-w-[60px] bg-background">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="25">25</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="all">All</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[12px] text-muted-foreground">
                      {pageSize === 'all'
                        ? `${totalListRows} row${totalListRows !== 1 ? 's' : ''}`
                        : `${(safeListPage - 1) * pageSize + 1}–${Math.min(safeListPage * pageSize, totalListRows)} of ${totalListRows}`}
                    </span>
                    {pageSize !== 'all' && totalListPages > 1 && (
                      <>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          disabled={safeListPage <= 1}
                          onClick={() => setCurrentListPage((p) => Math.max(1, p - 1))}
                          aria-label="Previous page"
                        >
                          <ChevronUp className="h-3.5 w-3.5 -rotate-90" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          disabled={safeListPage >= totalListPages}
                          onClick={() => setCurrentListPage((p) => Math.min(totalListPages, p + 1))}
                          aria-label="Next page"
                        >
                          <ChevronDown className="h-3.5 w-3.5 -rotate-90" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Future gaps section ────────────────────────────────────── */}
          {data.futureGaps.length > 0 && (
            <div
              className="rounded-[var(--radius-md)] border border-border overflow-hidden"
              style={{ background: 'var(--card)' }}
            >
              {/* Section header */}
              <div
                className="flex items-center gap-2 px-4 py-3 border-b border-border"
                style={{ background: 'var(--muted)' }}
              >
                <AlertTriangle className="h-3.5 w-3.5 text-warning-500 shrink-0" />
                <span className="text-[13px] font-medium text-foreground">
                  Upcoming unfilled roles
                </span>
                <span className="text-[12px] text-muted-foreground">
                  · {data.futureGaps.length} role
                  {data.futureGaps.length !== 1 ? 's' : ''} requiring attention
                </span>
              </div>

              {/* Future gap rows */}
              <div className="divide-y divide-border/50">
                {data.futureGaps.map((gap, i) => {
                  const urgencyClass =
                    gap.daysUntilStart < 14
                      ? 'text-destructive font-medium'
                      : gap.daysUntilStart < 28
                      ? 'text-warning-500 font-medium'
                      : 'text-muted-foreground';

                  return (
                    <div
                      key={`${gap.projectId}-${gap.roleName}-${i}`}
                      className="flex items-center gap-4 px-4 py-2.5 flex-wrap"
                    >
                      <span className="text-[13px] font-medium text-foreground min-w-[160px]">
                        {gap.projectName}
                      </span>
                      <span className="text-[12px] text-muted-foreground min-w-[110px]">
                        {gap.roleName}
                      </span>
                      <span className="text-[12px] text-muted-foreground whitespace-nowrap">
                        {gap.startDate ? format(gap.startDate, 'dd MMM yyyy') : '—'}
                      </span>
                      <span className={`text-[12px] whitespace-nowrap ${urgencyClass}`}>
                        {gap.daysUntilStart} days
                      </span>
                      <button
                        onClick={() => onNavigate?.('report-available-capacity')}
                        className="text-[12px] text-primary font-medium hover:underline ml-auto whitespace-nowrap"
                      >
                        Find resource ›
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ── Row detail drawer (list view) ──────────────────────────────── */}
      <RowDetailDrawer
        req={drawerReq}
        project={drawerProject}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onAddResource={handleAddResource}
        onNavigateToAvailableCapacity={handleNavigateToAvailableCapacity}
        onRemoveResource={handleRemoveResource}
      />

      {/* ── Add booking modal ────────────────────────────────────────────── */}
      <ComprehensiveBookingModal
        open={bookingModalOpen}
        onOpenChange={(o) => {
          setBookingModalOpen(o);
          if (!o) setBookingModalReq(null);
        }}
        onSave={async (bookingData) => {
          await addBooking(bookingData);
          setBookingModalOpen(false);
          setBookingModalReq(null);
          toast.success('Booking added', {
            description: `Resource booked to ${bookingModalProjectName}.`,
          });
          // Refetch live bookings so the report reflects the new booking
          if (currentMode === 'live') {
            fetchLiveBookings(dateFrom, dateTo);
          }
        }}
        preselectedBookingData={{
          projectName: bookingModalProjectName,
          projectId: bookingModalReq?.projectId,
          requiredRole: bookingModalReq?.roleName,
        }}
        resources={resources}
        projectDetails={projectDetails}
        existingBookings={bookings}
        mode="create"
      />
    </div>
  );
}
